"use client";
import type { RowData } from "@tanstack/react-table";

import { useMemo } from "react";
import { getAvailableClientPage } from "./grid/client-page";
import { getClientPageRowCount } from "./record-table-support";
import { useRecordWindow } from "./state/use-record-window";
import type {
  RecordDataSource,
  RecordPresentationMode,
  RecordTableState,
  RecordTotal,
} from "./types";

const UNKNOWN_TOTAL: RecordTotal = { kind: "unknown" };

function useRecordTotal<TData extends RowData>(
  data: RecordDataSource<TData>,
): RecordTotal {
  const providedTotal =
    data.mode === "offset" || data.mode === "cursor" ? data.total : undefined;
  const clientTotal = data.mode === "client" ? data.rows.length : undefined;
  return useMemo<RecordTotal>(
    () =>
      providedTotal ??
      (clientTotal === undefined
        ? UNKNOWN_TOTAL
        : { kind: "exact", value: clientTotal }),
    [clientTotal, providedTotal],
  );
}

export function useRecordTableRows<TData extends RowData>(
  data: RecordDataSource<TData>,
  state: RecordTableState,
  infiniteScroll: boolean,
  presentationMode: RecordPresentationMode,
) {
  const total = useRecordTotal(data);
  const recordWindow = useRecordWindow(data, state, infiniteScroll);
  const { rows } = recordWindow;
  const page =
    data.mode === "client"
      ? getAvailableClientPage(rows.length, state.page, state.pageSize)
      : recordWindow.page;
  const paginationRowCount =
    data.mode === "client"
      ? getClientPageRowCount(rows.length, page, state.pageSize)
      : rows.length;
  const shouldShowPagination =
    data.mode !== "none" &&
    !(data.mode === "client" && data.paginate === false) &&
    presentationMode === "table" &&
    !infiniteScroll &&
    !(rows.length === 0 && page <= 1);
  return {
    ...recordWindow,
    page,
    total,
    paginationRowCount,
    shouldShowPagination,
  };
}
